/**
 * ProjectStatusBadge — badge cohérent pour les 8 statuts Projects.
 *
 * Mapping enum `app.project_status` → couleur slate/amber/brand/violet/emerald
 * pour signaler visuellement la phase du projet :
 *   - draft / archived  → slate (inactif / neutre)
 *   - collecting / ingesting → amber (en cours collecte)
 *   - analyzing / reporting → brand-blue (en cours analyse)
 *   - sent              → violet (envoyé client)
 *   - approved          → emerald (validé)
 *
 * Pattern parité `CompanyStatusBadge` (active/archived) — ici 8 valeurs.
 * Optionnel `pulseActive` pour animer le dot quand status actif "vivant"
 * (collecting/ingesting/analyzing/reporting).
 */

import { useTranslation } from "react-i18next";

import type { ProjectRow } from "@kollectivia/shared";

type Tone = "slate" | "amber" | "brand" | "violet" | "emerald";

const TONE: Record<Tone, { bg: string; text: string; dot: string }> = {
  slate: { bg: "bg-slate-100", text: "text-slate-700", dot: "bg-slate-400" },
  amber: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  brand: { bg: "bg-brand-50", text: "text-brand-700", dot: "bg-brand-500" },
  violet: { bg: "bg-violet-50", text: "text-violet-700", dot: "bg-violet-500" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500" },
};

function toneFor(status: ProjectRow["status"]): Tone {
  switch (status) {
    case "draft":
      return "slate";
    case "collecting":
    case "ingesting":
      return "amber";
    case "analyzing":
    case "reporting":
      return "brand";
    case "sent":
      return "violet";
    case "approved":
      return "emerald";
    case "archived":
      return "slate";
    default:
      return "slate";
  }
}

function isLiveStatus(status: ProjectRow["status"]): boolean {
  return (
    status === "collecting" ||
    status === "ingesting" ||
    status === "analyzing" ||
    status === "reporting"
  );
}

type Props = {
  status: ProjectRow["status"];
  /** Anime le dot subtilement pour les statuts vivants (collecting/ingesting/analyzing/reporting). */
  pulseLive?: boolean;
};

export function ProjectStatusBadge({ status, pulseLive = false }: Props) {
  const { t } = useTranslation();
  const tone = TONE[toneFor(status)];
  const pulse = pulseLive && isLiveStatus(status);
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full ${tone.bg} px-2.5 py-0.5 text-xs font-medium ${tone.text}`}
    >
      <span
        aria-hidden="true"
        className={`h-1.5 w-1.5 rounded-full ${tone.dot} ${pulse ? "motion-safe:animate-pulse motion-reduce:animate-none" : ""}`}
      />
      {t(`projects.statuses.${status}`)}
    </span>
  );
}
